// index.tsx
//
// (c) NCR Voyix Corporation 2025. All rights reserved.

import React, { useEffect, useState } from 'react'
import {
  Button, Stack, Typography
} from '@mui/material'
import { connect } from 'react-redux'
import { Navigate, useNavigate } from 'react-router-dom'
import dayjs from 'dayjs'
import { AppThunkDispatch, RootState } from '../../redux'
import DeploymentSitesTable from './components/deploymentSitesTable'
import {
  DeploymentSaveStatus,
  resetDeploymentLiteState,
  setRetrySaveStatus,
  setSaveInProgress
} from '../../redux/deploymentLite/slice'
import ContinueDialogBox from '../../components/ContinueDialogBox'
import ConfirmDeploymentSitesModal from './components/confirmDeploymentSites'
import { SaveDeploymentRequest } from '../../dtos/deployment/saveDeploymentRequest'
import { saveDeploymentLite } from '../../redux/deploymentLite/thunks'
import { TextBox } from '../../components/TextBox'
import Notification from '../../components/Notification'
import { SavedDeploymentConfirmation } from './components/savedDeploymentConfirmation'

function mapDispatchToProps(dispatch: AppThunkDispatch) {
  return {
    dispatchResetDeploymentLiteState: (...args: Parameters<typeof resetDeploymentLiteState>) => dispatch(resetDeploymentLiteState(...args)),
    dispatchSetRetrySaveStatus: (...args: Parameters<typeof setRetrySaveStatus>) => dispatch(setRetrySaveStatus(...args)),
    dispatchSaveDeployment: (...args: Parameters<typeof saveDeploymentLite>) => dispatch(saveDeploymentLite(...args)),
    dispatchSetSaveInProgress: (...args: Parameters<typeof setSaveInProgress>) => dispatch(setSaveInProgress(...args))
  }
}

function mapStateToProps(state: RootState) {
  return {
    solutionSet: state.deploymentLite.solutionSet,
    selectedSites: state.deploymentLite.deploymentSitesIds,
    userId: state.user.userId,
    username: state.user.userRealName,
    datacenter: state.user.dataCenter,
    sessionId: state.user.sessionId,
    companyId: state.company.selectedCompany?.id,
    saveStatus: state.deploymentLite.deploymentSaveStatus
  }
}

type StateProps = ReturnType<typeof mapStateToProps>
type DispatchProps = ReturnType<typeof mapDispatchToProps>
type DeploymentLiteProps = StateProps & DispatchProps

function DeploymentLite(
  {
    solutionSet,
    selectedSites,
    dispatchResetDeploymentLiteState,
    userId,
    username,
    datacenter,
    companyId,
    sessionId,
    dispatchSaveDeployment,
    saveStatus,
    dispatchSetRetrySaveStatus,
    dispatchSetSaveInProgress
  }: Readonly<DeploymentLiteProps>
): JSX.Element {
  const navigate = useNavigate()
  const [isDiscardDialogOpen, setIsDiscardDialogOpen] = useState<boolean>(false)
  const [isConfirmSitesDialogOpen, setIsConfirmSitesDialogOpen] = useState<boolean>(false)
  const [isSendButtonDisabled, setIsSendButtonDisabled] = useState<boolean>(true)
  const [deploymentName, setDeploymentName] = useState<string>(`${solutionSet?.name} ${dayjs(new Date()).format('YYYYMMDD')}`)

  useEffect(() => {
    if (saveStatus === DeploymentSaveStatus.Success) {
      setIsConfirmSitesDialogOpen(false)
    }
  }, [saveStatus])

  useEffect(
    () => setIsSendButtonDisabled(deploymentName.length === 0 || selectedSites.length === 0),
    [deploymentName.length, selectedSites.length]
  )

  if (solutionSet?.id == null) {
    return <Navigate to="/solutionsets" replace />
  }
  const handleDiscardChanges = () => {
    dispatchResetDeploymentLiteState()
    navigate('/solutionsets')
  }

  const handleConfirmedSave = () => {
    dispatchSetSaveInProgress()
    const deployment: SaveDeploymentRequest = {
      name: deploymentName,
      solutionSet: {
        id: solutionSet.id,
        name: solutionSet.name,
        products: solutionSet.products.reduce<{[key: string]: string}>((acc, p) => {
          acc[p.name] = p.version
          return acc
        }, {})
      },
      createdBy: {
        id: userId as string,
        name: username as string
      },
      sites: selectedSites.map((s) => ({
        id: s
      }))
    }

    if (
      userId
      && username
      && datacenter
      && companyId
      && sessionId
    ) {
      dispatchSaveDeployment({
        userId,
        datacenter,
        companyId,
        sessionId,
        deployment
      })
    }
  }

  return (
    <>
      <Stack
        alignItems="baseline"
        spacing={4}
        sx={{
          width: '100%'
        }}
      >
        <Typography variant="h4">
          {`Deploy ${solutionSet?.name} Configuration`}
        </Typography>
        <Stack
          sx={{
            width: '50%'
          }}
        >
          <TextBox
            label="Name"
            variant="standard"
            fullWidth
            initialValue={`${solutionSet?.name} ${dayjs(new Date()).format('YYYYMMDD')}`}
            onChange={(value) => setDeploymentName(value)}
            characterLimit={256}
          />
        </Stack>
        <Stack
          width="100%"
        >
          <DeploymentSitesTable />
          <Stack
            direction="row"
            width="100%"
            justifyContent="space-between"
            paddingTop={3}
          >
            <Button
              variant="outlined"
              color="primary"
              onClick={() => {
                setIsDiscardDialogOpen(true)
              }}
            >
              Discard Changes
            </Button>
            <Button
              variant="contained"
              color="primary"
              disabled={isSendButtonDisabled}
              onClick={() => {
                setIsConfirmSitesDialogOpen(true)
              }}
            >
              Send Now
            </Button>
          </Stack>
        </Stack>
      </Stack>

      <SavedDeploymentConfirmation
        open={saveStatus === DeploymentSaveStatus.Success}
        onConfirm={() => {
          dispatchResetDeploymentLiteState()
        }}
      />
      <Notification
        open={saveStatus === DeploymentSaveStatus.Failed}
        onClose={() => {
          dispatchSetRetrySaveStatus()
        }}
        autoHideSeconds={2}
        severity="error"
        message="Unable to save new deployment"
        data-testid="notification-deployment-set-saved-error"
      />

      <ConfirmDeploymentSitesModal
        open={isConfirmSitesDialogOpen}
        isSaving={saveStatus === DeploymentSaveStatus.Pending}
        onCancel={() => {
          setIsConfirmSitesDialogOpen(false)
        }}
        onContinue={handleConfirmedSave}
      />

      <ContinueDialogBox
        open={isDiscardDialogOpen}
        title="Discard?"
        description="Are you sure you want to discard the current deployment?"
        cancelButtonText="No"
        continueButtonText="Yes"
        onCancel={() => {
          setIsDiscardDialogOpen(false)
        }}
        onContinue={() => {
          setIsDiscardDialogOpen(false)
          handleDiscardChanges()
        }}
      />
    </>
  )
}

export default connect(mapStateToProps, mapDispatchToProps)(DeploymentLite)
